import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { useEffect } from 'react';
import {
  Home,
  LogOut,
  Inbox,
  Building2,
  UserSquare2,
  Users,
  HardHat,
  ScrollText,
  ShieldCheck,
  Bell,
  ClipboardList,
  DoorOpen,
  Radio,
  type LucideIcon,
} from 'lucide-react';

import { useAdminStatsQuery } from '@/features/admin/api';
import { useLogoutMutation } from '@/features/auth/api';
import { BrandLogo } from '@/shared/branding';
import { PoweredBy } from '@/shared/components/PoweredBy';
import { Button, IconButton, cn } from '@/shared/components/ui';
import { MOTION } from '@/shared/motion';
import { useAuthStore } from '@/shared/stores/authStore';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  exact?: boolean;
  /// Only shown to SUPER_ADMINs (the route + API enforce it too).
  superAdminOnly?: boolean;
  /// Hidden from VIEWERs (they lack the permission the page needs).
  notForViewer?: boolean;
  /// Kept in the route table (still reachable by URL, still titled) but hidden
  /// from the sidebar — e.g. the gate entry log, whose data now lives in the
  /// community app, so the page is intentionally not surfaced for now.
  hidden?: boolean;
}

interface NavGroup {
  label: string;
  items: readonly NavItem[];
}

const CORE: readonly NavItem[] = [{ to: '/admin', label: 'Dashboard', icon: Home, exact: true }];

const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: 'Management',
    items: [
      { to: '/admin/submissions', label: 'Submissions', icon: Inbox },
      { to: '/admin/units', label: 'Units', icon: Building2 },
      { to: '/admin/owners', label: 'Owners', icon: UserSquare2 },
      { to: '/admin/users', label: 'Delegated users', icon: Users },
      { to: '/admin/workers', label: 'Workers', icon: HardHat },
      // Hidden for now — gate logs are tracked in the community app, so this
      // page reads empty here. Kept routable for a future log integration.
      { to: '/admin/entry-log', label: 'Entry log', icon: DoorOpen, hidden: true },
    ],
  },
  {
    label: 'System',
    items: [
      { to: '/admin/reminders', label: 'Reminders', icon: Bell, notForViewer: true },
      { to: '/admin/message-log', label: 'Message tracker', icon: Radio },
      { to: '/admin/audit-logs', label: 'Audit log', icon: ScrollText },
      {
        to: '/admin/form-details',
        label: 'Form details',
        icon: ClipboardList,
        superAdminOnly: true,
      },
      { to: '/admin/team', label: 'Admin accounts', icon: ShieldCheck, superAdminOnly: true },
    ],
  },
];

const NAV_FLAT = [...CORE, ...NAV_GROUPS.flatMap((g) => g.items)];
const TITLES: Record<string, string> = Object.fromEntries(NAV_FLAT.map((i) => [i.to, i.label]));

/// Hide role-gated items for everyone else. Belt-and-braces with the route
/// guards + API permission checks.
function visibleGroups(isSuperAdmin: boolean, isViewer: boolean): readonly NavGroup[] {
  return NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter(
      (i) => !i.hidden && (!i.superAdminOnly || isSuperAdmin) && (!i.notForViewer || !isViewer),
    ),
  })).filter((g) => g.items.length > 0);
}

const navLinkBase =
  'group relative flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] transition-colors duration-150';

function NavButton({ item, active, badge }: { item: NavItem; active: boolean; badge?: number }) {
  return (
    <Link
      to={item.to}
      activeOptions={{ exact: !!item.exact }}
      className={cn(
        navLinkBase,
        active
          ? 'font-medium text-accent-soft-foreground [&_svg]:text-accent'
          : 'text-foreground-muted hover:bg-surface-muted hover:text-foreground [&_svg]:text-foreground-subtle group-hover:[&_svg]:text-foreground-muted',
      )}
    >
      {active && (
        <motion.span
          layoutId="navActivePill"
          transition={MOTION.spring.soft}
          className="absolute inset-0 -z-10 rounded-lg bg-brand-soft ring-1 ring-inset ring-brand/15"
        />
      )}
      <item.icon className="relative size-4 shrink-0" />
      <span className="relative truncate">{item.label}</span>
      {badge !== undefined && badge > 0 && (
        <span className="relative ms-auto rounded-full bg-[image:var(--gradient-brand)] px-1.5 py-0.5 text-[10px] font-semibold leading-none text-brand-foreground shadow-glow-brand">
          {badge}
        </span>
      )}
    </Link>
  );
}

/** Active-route test that mirrors TanStack's exact/prefix matching. */
function isActivePath(pathname: string, to: string, exact?: boolean) {
  return exact ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);
}

export const Route = createFileRoute('/admin')({
  beforeLoad: () => {
    const state = useAuthStore.getState();
    if (!state.isAuthenticated || state.user?.role !== 'admin') {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router contract: beforeLoad throws a redirect object, not an Error.
      throw redirect({ to: '/admin/login' });
    }
  },
  component: AdminShell,
});

const ROLE_LABELS: Record<string, string> = {
  VIEWER: 'Viewer',
  REVIEWER: 'Reviewer',
  MODERATOR: 'Moderator',
  SUPER_ADMIN: 'Super admin',
};

function AdminShell() {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const navigate = useNavigate();
  const logout = useLogoutMutation();

  // Session cleared mid-session (token rejected + refresh failed) → bounce to
  // the admin login immediately rather than leaving a dead console open.
  useEffect(() => {
    if (!isAuthenticated) void navigate({ to: '/admin/login' });
  }, [isAuthenticated, navigate]);
  const stats = useAdminStatsQuery();
  const pending = stats.data?.pendingSubmissions ?? 0;

  const isSuperAdmin = user?.adminRole === 'SUPER_ADMIN';
  const groups = visibleGroups(isSuperAdmin, user?.adminRole === 'VIEWER');
  const flat = [...CORE, ...groups.flatMap((g) => g.items)];
  const roleLabel = user?.adminRole ? (ROLE_LABELS[user.adminRole] ?? 'Admin') : 'Admin';

  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const pageTitle = TITLES[pathname] ?? 'Admin';

  const initials =
    user?.fullName
      ?.split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join('') ?? 'A';
  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });

  const signOut = () => {
    void logout.mutateAsync().finally(() => {
      void navigate({ to: '/admin/login' });
    });
  };

  const badgeFor = (to: string) => (to === '/admin/submissions' ? pending : undefined);

  return (
    // Admin is an English-only internal console (public/resident UI stays
    // Arabic RTL). Force LTR here so labels, tables, and the sidebar read
    // left-to-right instead of being mirrored by the global dir="rtl".
    <div dir="ltr" className="flex min-h-screen bg-canvas text-foreground">
      {/* Sidebar ---------------------------------------------------------- */}
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-e border-border bg-surface-glass backdrop-blur-xl lg:flex">
        <div className="shrink-0 px-5 py-[18px]">
          {/* Box = the sidebar's full inner width (240px shell − 2×20px padding)
              by 80px, so a wide lockup spans the sidebar and a stacked one grows
              to the height limit instead of shrinking to a stamp. */}
          <BrandLogo className="max-h-20 w-auto max-w-[12.5rem] object-contain" />
        </div>

        <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto px-3 pb-3 pt-1">
          <div className="space-y-0.5">
            <p className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-subtle">
              Overview
            </p>
            {CORE.map((item) => (
              <NavButton
                key={item.to}
                item={item}
                active={isActivePath(pathname, item.to, item.exact)}
              />
            ))}
          </div>

          {groups.map((group) => (
            <div key={group.label} className="space-y-0.5 pt-4">
              <p className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-subtle">
                {group.label}
              </p>
              {group.items.map((item) => (
                <NavButton
                  key={item.to}
                  item={item}
                  active={isActivePath(pathname, item.to, item.exact)}
                  badge={badgeFor(item.to)}
                />
              ))}
            </div>
          ))}
        </nav>

        <div className="shrink-0 border-t border-border p-3">
          <div className="flex items-center gap-2.5 rounded-lg bg-surface px-2.5 py-2 ring-1 ring-hairline shadow-e1">
            <div
              aria-hidden
              className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[image:var(--gradient-brand)] text-xs font-semibold text-brand-foreground shadow-glow-brand"
            >
              {initials}
            </div>
            <div className="flex min-w-0 flex-1 flex-col leading-tight">
              <span className="truncate text-xs font-semibold">{user?.fullName ?? 'Admin'}</span>
              <span className="truncate text-[10px] text-foreground-subtle">{roleLabel}</span>
            </div>
            <IconButton label="Sign out" size="sm" variant="destructive" onClick={signOut}>
              <LogOut />
            </IconButton>
          </div>
          <div className="mt-2 flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </aside>

      {/* Main column ------------------------------------------------------ */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar (desktop) */}
        <header className="sticky top-0 z-20 hidden h-14 items-center justify-between gap-6 border-b border-border bg-surface-glass px-7 backdrop-blur-xl lg:flex">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="truncate text-sm font-semibold tracking-tight">{pageTitle}</span>
            <span className="size-1 rounded-full bg-border-strong" aria-hidden />
            <span className="truncate text-[13px] text-foreground-muted">{today}</span>
          </div>

          <div className="flex shrink-0 items-center gap-3">
            <span className="text-[13px] font-medium text-foreground-muted">
              {user?.fullName ?? 'Admin'}
            </span>
            <IconButton label="Sign out" variant="secondary" onClick={signOut}>
              <LogOut />
            </IconButton>
          </div>
        </header>

        {/* Mobile top bar */}
        <header className="flex h-14 items-center justify-between gap-3 border-b border-border bg-surface px-4 lg:hidden">
          <BrandLogo className="max-h-9 w-auto max-w-[7.5rem] object-contain" />
          <Button
            variant="ghost"
            size="sm"
            startIcon={<LogOut className="size-4" />}
            onClick={signOut}
          >
            Sign out
          </Button>
        </header>

        {/* Mobile horizontal nav strip (sidebar collapses on small screens) */}
        <nav className="overflow-x-auto border-b border-border bg-surface px-3 py-2 lg:hidden">
          <ul className="flex items-center gap-1 whitespace-nowrap">
            {flat.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  activeOptions={{ exact: !!item.exact }}
                  className={cn(
                    'inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                    'text-foreground-muted hover:bg-surface-muted hover:text-foreground',
                  )}
                  activeProps={{
                    className: 'bg-accent-soft text-accent-soft-foreground [&_svg]:text-accent',
                  }}
                >
                  <item.icon className="size-3.5" />
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-7 lg:py-7">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
